import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Landmark, Bitcoin, ExternalLink, ShieldCheck, Loader2, KeyRound, CheckCircle2, Printer } from "lucide-react";
import { api, apiError, setSupporterCode } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import Reveal from "@/components/Reveal";

const CRYPTO_META = [
  { key: "btc", label: "Bitcoin", ticker: "BTC", testid: "crypto-copy-btc" },
  { key: "eth", label: "Ethereum", ticker: "ETH", testid: "crypto-copy-eth" },
  { key: "sol", label: "Solana", ticker: "SOL", testid: "crypto-copy-sol" },
  { key: "usdt", label: "Tether", ticker: "USDT · TRC20", testid: "crypto-copy-usdt" },
];

export default function Donate() {
  const { settings, stats, unlocked, setUnlocked, refresh } = useSite();
  const [ackEmail, setAckEmail] = useState("");
  const [ackBusy, setAckBusy] = useState(false);
  const [code, setCode] = useState(null);
  const [chain, setChain] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/bitcoin/balance").then((r) => alive && setChain(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const chainSats = chain ? chain.confirmed_sats + chain.mempool_sats : 0;
  const chainUsd = chain ? (chainSats / 100_000_000) * chain.btc_usd : 0;
  const raised = (stats?.raised || 0) + chainUsd;
  const goal = stats?.goal || settings?.donation_goal || 25000;
  const pct = Math.min(100, Math.round((raised / goal) * 100));

  const claimCode = async (e) => {
    e.preventDefault();
    setAckBusy(true);
    try {
      const { data } = await api.post("/donations/ack", { email: ackEmail });
      setCode(data.supporter_code);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Could not retrieve supporter code"));
    } finally {
      setAckBusy(false);
    }
  };

  const activate = () => {
    if (!code) return;
    setSupporterCode(code);
    setUnlocked(true);
    toast.success("Supporter access activated on this device.");
  };

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard`);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };

  return (
    <main className="min-h-screen">
      <section className="relative hero-glow noise-overlay py-16 sm:py-20 border-b border-[#1E2638] overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber-400 mb-4">Fund the Hardware — Build the Sovereign Future</p>
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl max-w-3xl leading-tight">
            Atomic-scale fabrication, funded on-chain.
          </h1>
          <p className="mt-5 text-slate-400 max-w-2xl leading-relaxed">
            Legacy engineering is obsolete. Traditional software layers, fragile silicon
            chips, and soldered wiring cannot survive extreme operational demands. We are
            building autonomous hardware governed by physical lattice geometry — not
            vulnerable external code.
          </p>
          <p className="mt-4 text-slate-400 max-w-2xl leading-relaxed">
            <strong className="text-slate-200">The objective:</strong> raise $1,750,000 in
            BTC to secure the production-grade multi-tip atomic deposition printer — a
            benchtop system depositing carbon-hydrogen-gold structural lattices, GaN neural
            pathways, and carbon-nanotube muscle hinges in a single unified build.
          </p>
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.2em] text-sky-400">
            No VCs. No fiat. No gatekeepers. Fund the core mechanics — own the physical grid.
          </p>

          <div data-testid="donation-goal-progress" className="mt-10 max-w-2xl">
            <div className="flex items-end justify-between mb-3">
              <p className="font-display font-bold text-2xl">
                ${raised.toLocaleString()} <span className="text-slate-500 text-base font-normal">raised</span>
              </p>
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500">
                Goal ${goal.toLocaleString()} · {pct}%
              </p>
            </div>
            <div className="h-2.5 rounded-full bg-[#131824] border border-[#1E2638] overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(pct, 2)}%` }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                className="h-full rounded-full bg-gradient-to-r from-sky-400 to-amber-400"
              />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-600">
              {stats?.supporters || 0} supporters so far · every amount counts
            </p>
            {chain && (
              <p data-testid="btc-live-balance" className="mt-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/80">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                Live on-chain wallet: {(chainSats / 100_000_000).toFixed(6)} BTC ≈ ${Math.round(chainUsd).toLocaleString()}
                {chain.stale ? " (last known)" : " · auto-updates"}
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Bitcoin — primary funding channel */}
          <Reveal>
            <div className="rounded-2xl border border-amber-400/40 bg-[#131824] p-8 h-full" data-testid="bitcoin-donation-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
                  <Bitcoin className="w-5 h-5 text-amber-400" />
                </span>
                <div>
                  <h2 className="font-display font-bold text-xl">Donate Bitcoin</h2>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400/70">Primary funding channel</p>
                </div>
              </div>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                100% of donations arrive in Bitcoin — no payment processors, no middlemen,
                no permission needed. Every satoshi funds open research directly.
              </p>
              {settings?.btc && (
                <div className="flex flex-col sm:flex-row items-center gap-6 mb-6">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=8&data=bitcoin:${settings.btc}`}
                    alt="Bitcoin donation QR code"
                    data-testid="btc-qr-code"
                    className="w-40 h-40 rounded-xl border border-[#1E2638] bg-white p-1 shrink-0"
                  />
                  <div className="flex-1 w-full">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-2">BTC address</p>
                    <button
                      onClick={() => copy(settings.btc, "Bitcoin address")}
                      data-testid="crypto-copy-btc"
                      className="w-full group flex items-center justify-between gap-3 rounded-lg border border-[#1E2638] bg-[#07090E] px-4 py-3.5 hover:border-amber-400/50 transition-colors text-left"
                    >
                      <span className="font-mono text-xs text-slate-300 break-all">{settings.btc}</span>
                      <Copy className="w-4 h-4 shrink-0 text-slate-600 group-hover:text-amber-400 transition-colors" />
                    </button>
                  </div>
                </div>
              )}
              <Link
                to="/poster"
                data-testid="donate-poster-link"
                className="mb-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 hover:text-amber-400 transition-colors"
              >
                <Printer className="w-3.5 h-3.5" /> Print the funding poster
              </Link>
              <div className="border-t border-[#1E2638] pt-6">
                {!code ? (
                  <form onSubmit={claimCode} className="space-y-3" data-testid="donation-ack-form">
                    <p className="text-xs text-slate-400">
                      Sent your donation? Leave an email (optional) and reveal your supporter code.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="email"
                        value={ackEmail}
                        onChange={(e) => setAckEmail(e.target.value)}
                        placeholder="you@domain.com (optional)"
                        data-testid="donation-ack-email"
                        className="flex-1 bg-[#07090E] border border-[#1E2638] rounded-md px-3.5 py-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:border-amber-400/60 transition-colors"
                      />
                      <button
                        type="submit"
                        disabled={ackBusy}
                        data-testid="donation-ack-submit"
                        className="bg-amber-400 text-[#07090E] font-semibold text-sm rounded-md px-5 py-2.5 hover:bg-amber-300 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {ackBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                        I've donated — get my code
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="rounded-xl border border-amber-400/40 bg-amber-400/5 p-5 text-center" data-testid="ack-code-card">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                    <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">Your supporter code</p>
                    <p data-testid="ack-supporter-code" className="font-mono text-xl font-bold tracking-[0.2em] text-amber-400 mb-4">{code}</p>
                    <button
                      onClick={activate}
                      data-testid="ack-activate-btn"
                      className="inline-flex items-center gap-2 bg-amber-400 text-[#07090E] font-semibold text-sm rounded-full px-5 py-2.5 hover:bg-amber-300 transition-colors"
                    >
                      <ShieldCheck className="w-4 h-4" /> Activate on this device
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Reveal>

          {/* Crypto */}
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-[#1E2638] bg-[#131824] p-8 h-full" data-testid="crypto-donation-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-lg bg-amber-400/10 border border-amber-400/40 flex items-center justify-center">
                  <Bitcoin className="w-5 h-5 text-amber-400" />
                </span>
                <h2 className="font-display font-bold text-xl">Crypto wallets</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                Prefer on-chain? Send any amount to a fund wallet, then use the supporter
                code to unlock deep dives.
              </p>
              <div className="space-y-3">
                {CRYPTO_META.filter((c) => settings?.[c.key] && c.key !== "btc").map((c) => (
                  <button
                    key={c.key}
                    onClick={() => copy(settings[c.key], `${c.label} address`)}
                    data-testid={c.testid}
                    className="w-full group flex items-center justify-between gap-4 rounded-lg border border-[#1E2638] bg-[#07090E] px-4 py-3.5 hover:border-amber-400/50 transition-colors text-left"
                  >
                    <div className="min-w-0">
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-amber-400 mb-1">
                        {c.label} · {c.ticker}
                      </p>
                      <p className="font-mono text-xs text-slate-400 truncate">{settings[c.key]}</p>
                    </div>
                    <Copy className="w-4 h-4 shrink-0 text-slate-600 group-hover:text-amber-400 transition-colors" />
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {/* PayPal */}
          <Reveal delay={0.15}>
            <div className="rounded-2xl border border-[#1E2638] bg-[#131824] p-8" data-testid="paypal-donation-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-lg bg-sky-400/10 border border-sky-400/40 flex items-center justify-center">
                  <ExternalLink className="w-5 h-5 text-sky-400" />
                </span>
                <h2 className="font-display font-bold text-xl">PayPal</h2>
              </div>
              <p className="text-sm text-slate-400 mb-6 leading-relaxed">
                One-tap donation through PayPal — whatever amount feels right.
              </p>
              <a
                href={settings?.paypal_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="paypal-donate-link"
                className="inline-flex items-center gap-2 bg-[#0070BA] text-white font-semibold text-sm rounded-full px-7 py-3.5 hover:brightness-110 transition-all"
              >
                Donate with PayPal <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </Reveal>

          {/* Bank */}
          <Reveal delay={0.2}>
            <div className="rounded-2xl border border-[#1E2638] bg-[#131824] p-8" data-testid="bank-donation-card">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-lg bg-emerald-400/10 border border-emerald-400/40 flex items-center justify-center">
                  <Landmark className="w-5 h-5 text-emerald-400" />
                </span>
                <h2 className="font-display font-bold text-xl">Bank transfer</h2>
              </div>
              {settings && (
                <dl className="space-y-3 text-sm mb-6">
                  {[
                    ["Bank", settings.bank_name],
                    ["Account name", settings.bank_account_name],
                    ["Account no.", settings.bank_account_number],
                    ["Routing", settings.bank_routing],
                    ["Reference", settings.bank_reference],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between gap-4 border-b border-[#1E2638] pb-2.5">
                      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{k}</dt>
                      <dd className="font-mono text-xs text-slate-200 truncate">{v}</dd>
                    </div>
                  ))}
                </dl>
              )}
              <button
                onClick={() =>
                  settings &&
                  copy(
                    `Bank: ${settings.bank_name}\nAccount name: ${settings.bank_account_name}\nAccount: ${settings.bank_account_number}\nRouting: ${settings.bank_routing}\nReference: ${settings.bank_reference}`,
                    "Bank details"
                  )
                }
                data-testid="bank-details-copy"
                className="inline-flex items-center gap-2 border border-[#1E2638] text-slate-300 font-semibold text-sm rounded-full px-6 py-3 hover:border-emerald-400/50 hover:text-emerald-300 transition-colors"
              >
                <Copy className="w-4 h-4" /> Copy wire details
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">
            Donations arrive in Bitcoin and crypto · 100% funds open research · no payment processors
          </p>
        </Reveal>
      </section>
    </main>
  );
}
